import React, { useState, useEffect, useContext } from 'react';
import { api as axios } from '../api/api';
import { SnackbarContext } from '../App';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
    SheetFooter,
    SheetClose,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
    UserPlus, Mail, Building, Briefcase, Phone,
    Tag as TagIcon, Search, Loader2
} from "lucide-react";

export default function ContactCreateModal({ isOpen, onClose, onSuccess, initialCompanyId, initialCompanyName }) {
    const showSnackbar = useContext(SnackbarContext);
    const [loading, setLoading] = useState(false);
    const [companies, setCompanies] = useState([]);

    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        title: '',
        phone: '',
        location: '',
        companyId: initialCompanyId || '',
        companyName: initialCompanyName || '',
        companyDomain: '',
        stage: 'LEAD',
        tags: '',
        notes: ''
    });

// eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (isOpen) {
            setForm({
                firstName: '',
                lastName: '',
                email: '',
                title: '',
                phone: '',
                location: '',
                companyId: initialCompanyId || '',
                companyName: initialCompanyName || '',
                companyDomain: '',
                stage: 'LEAD',
                tags: '',
                notes: ''
            });
            fetchRecentCompanies();
        }
    }, [isOpen, initialCompanyId, initialCompanyName]);

    const fetchRecentCompanies = async () => {
        try {
            const res = await axios.get('/api/crm/companies?limit=10');
            setCompanies(res.data.data || []);
        } catch (e) { /* silent fail */ }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.email || !form.email.includes('@')) {
            showSnackbar('Valid email is required', 'error');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                ...form,
                tags: form.tags.split(',').map(t => t.trim()).filter(Boolean)
            };
            
            await axios.post('/api/contacts', payload);
            showSnackbar('Contact created successfully', 'success');
            if (onSuccess) onSuccess();
            onClose();
        } catch (error) {
            console.error('Error creating contact:', error);
            showSnackbar(error.response?.data?.message || 'Error creating contact', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Sheet open={isOpen} onOpenChange={onClose}>
            <SheetContent side="right" size="lg" className="p-0">
                <form onSubmit={handleSubmit}>
                    <SheetHeader className="p-6 bg-muted/20 border-b border-border/50">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                                <UserPlus className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <SheetTitle className="text-lg font-bold tracking-tight">Create Contact</SheetTitle>
                                <SheetDescription className="text-xs font-medium">
                                    Add a new lead to your workspace CRM.
                                </SheetDescription>
                            </div>
                        </div>
                    </SheetHeader>

                    <div className="p-6 grid grid-cols-2 gap-4">
                        {/* Identity Section */}
                        <div className="space-y-4 col-span-2">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="h-px flex-1 bg-border/50"></div>
                                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60 px-2 font-mono">Person Details</span>
                                <div className="h-px flex-1 bg-border/50"></div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-bold text-foreground/80">First Name</Label>
                                    <Input
                                        placeholder="John"
                                        value={form.firstName}
                                        onChange={e => setForm({...form, firstName: e.target.value})}
                                        className="h-9 text-sm focus-visible:ring-primary border-border bg-card/50"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-bold text-foreground/80">Last Name</Label>
                                    <Input
                                        placeholder="Doe"
                                        value={form.lastName}
                                        onChange={e => setForm({...form, lastName: e.target.value})}
                                        className="h-9 text-sm focus-visible:ring-primary border-border bg-card/50"
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs font-bold text-foreground/80 flex items-center gap-1.5">
                                    <Mail className="w-3 h-3 opacity-60" /> Email Address <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                    type="email"
                                    required
                                    placeholder="john@example.com"
                                    value={form.email}
                                    onChange={e => setForm({...form, email: e.target.value})}
                                    className="h-9 text-sm focus-visible:ring-primary border-border bg-card/50 font-medium"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-bold text-foreground/80 flex items-center gap-1.5">
                                        <Briefcase className="w-3 h-3 opacity-60" /> Job Title
                                    </Label>
                                    <Input
                                        placeholder="CEO"
                                        value={form.title}
                                        onChange={e => setForm({...form, title: e.target.value})}
                                        className="h-9 text-sm focus-visible:ring-primary border-border bg-card/50"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-bold text-foreground/80 flex items-center gap-1.5">
                                        <Phone className="w-3 h-3 opacity-60" /> Phone
                                    </Label>
                                    <Input
                                        placeholder="+1 234 567 890"
                                        value={form.phone}
                                        onChange={e => setForm({...form, phone: e.target.value})}
                                        className="h-9 text-sm focus-visible:ring-primary border-border bg-card/50"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Company Section */}
                        <div className="space-y-4 col-span-2 mt-2">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="h-px flex-1 bg-border/50"></div>
                                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60 px-2 font-mono">Company Link</span>
                                <div className="h-px flex-1 bg-border/50"></div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-bold text-foreground/80 flex items-center gap-1.5">
                                        <Building className="w-3 h-3 opacity-60" /> Account Match
                                    </Label>
                                    <Input
                                        placeholder="Company Name"
                                        value={form.companyName}
                                        onChange={e => setForm({...form, companyName: e.target.value, companyId: ''})}
                                        className="h-9 text-sm focus-visible:ring-primary border-border bg-card/50 font-bold"
                                    />
                                    {companies.length > 0 && !form.companyId && form.companyName && (
                                        <div className="text-[10px] text-muted-foreground mt-1 px-1">
                                            Auto-creates a new account if no match found.
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-bold text-foreground/80 flex items-center gap-1.5">
                                        <Search className="w-3 h-3 opacity-60" /> Website / Domain
                                    </Label>
                                    <Input
                                        placeholder="example.com"
                                        value={form.companyDomain}
                                        onChange={e => setForm({...form, companyDomain: e.target.value})}
                                        className="h-9 text-sm focus-visible:ring-primary border-border bg-card/50"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* CRM Meta Section */}
                        <div className="space-y-4 col-span-2 mt-2">
                             <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-bold text-foreground/80">CRM Stage</Label>
                                    <Select value={form.stage} onValueChange={v => setForm({...form, stage: v})}>
                                        <SelectTrigger className="h-9 border-border bg-card/50 text-xs font-bold">
                                            <SelectValue placeholder="Select Stage" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="LEAD" className="text-xs font-bold">LEAD</SelectItem>
                                            <SelectItem value="CONTACTED" className="text-xs font-bold">CONTACTED</SelectItem>
                                            <SelectItem value="REPLIED" className="text-xs font-bold">REPLIED</SelectItem>
                                            <SelectItem value="MEETING_BOOKED" className="text-xs font-bold">MEETING</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-bold text-foreground/80 flex items-center gap-1.5">
                                        <TagIcon className="w-3 h-3 opacity-60" /> Tags
                                    </Label>
                                    <Input
                                        placeholder="vip, saas, Q1"
                                        value={form.tags}
                                        onChange={e => setForm({...form, tags: e.target.value})}
                                        className="h-9 text-sm focus-visible:ring-primary border-border bg-card/50"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <SheetFooter className="p-4 bg-muted/20 border-t border-border/50 flex sm:justify-between items-center">
                        <p className="text-[10px] text-muted-foreground/60 italic hidden sm:block">
                            * Workspace isolation is preserved.
                        </p>
                        <div className="flex gap-2">
                            <SheetClose asChild>
                                <Button type="button" variant="outline" disabled={loading} className="h-9 px-4 text-xs font-bold uppercase tracking-widest border-border">
                                    Cancel
                                </Button>
                            </SheetClose>
                            <Button type="submit" disabled={loading} className="h-9 px-6 text-xs font-bold uppercase tracking-widest bg-primary hover:bg-primary/90 shadow-glow shadow-primary/20">
                                {loading ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Creating...</> : 'Save Contact'}
                            </Button>
                        </div>
                    </SheetFooter>
                </form>
            </SheetContent>
        </Sheet>
    );
}
